import { useTranslation } from 'react-i18next'
import { CustomerPipelineFilters } from '../types'
import { getConversionFunnel } from '../services'
import { Button, Typogaphy } from 'vnetwork-ui'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useMemo, useRef } from 'react'
import dayjs from 'dayjs'
import { Download, ImageDown, Clock } from 'lucide-react'
import * as XLSX from 'xlsx'
import html2canvas from 'html2canvas'
import { SCHEMES } from '@/components/EchartCustomized'

interface PipelineOverviewChartProps {
    filters: CustomerPipelineFilters
}

const STAGE_COLORS = {
    searching: SCHEMES.info[0],
    presentation: SCHEMES.brand[5],
    poc: SCHEMES.warning[0],
    contract: SCHEMES.brand[6],
    close_win: SCHEMES.success[0],
    close_lose: SCHEMES.error[0],
}

const PipelineOverviewChart: React.FC<PipelineOverviewChartProps> = ({
    filters,
}) => {
    const { t } = useTranslation('report')
    const chartRef = useRef<HTMLDivElement>(null)

    // Calculate previous period filters
    const previousFilters = useMemo(() => {
        if (!filters.gte || !filters.lte) return null

        const start = dayjs(filters.gte)
        const end = dayjs(filters.lte)
        const diffDays = end.diff(start, 'day') + 1

        return {
            ...filters,
            gte: start.subtract(diffDays, 'day').format('YYYY-MM-DD'),
            lte: start.subtract(1, 'day').format('YYYY-MM-DD'),
        }
    }, [filters])

    // Current period query
    const { data, renderState } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.REPORTS_SALE_PERFORMANCES,
            'pipeline-overview',
            filters.gte,
            filters.lte,
            filters.sales_manager_codes,
        ],
        queryFn: () => getConversionFunnel(filters),
        enabled: !!filters.gte && !!filters.lte,
    })

    // Previous period query
    const { data: previousData } = useQueryWithHandler(
        {
            queryKey: [
                QUERY_KEYS.REPORTS_SALE_PERFORMANCES,
                'pipeline-overview',
                previousFilters?.gte,
                previousFilters?.lte,
                previousFilters?.sales_manager_codes,
            ],
            queryFn: () => getConversionFunnel(previousFilters!),
            enabled: !!previousFilters?.gte && !!previousFilters?.lte,
        },
        {}
    )

    // Calculate growth rates
    const calculateGrowth = (current: number, previous: number | undefined) => {
        if (!previous || previous === 0) return null
        return ((current - previous) / previous) * 100
    }

    const growthRates = useMemo(() => {
        if (!data || !previousData) return null
        return {
            searching: calculateGrowth(
                data.summary.total_searching_records,
                previousData.summary.total_searching_records
            ),
            presentation: calculateGrowth(
                data.summary.total_presentation_records,
                previousData.summary.total_presentation_records
            ),
            poc: calculateGrowth(
                data.summary.total_poc_records,
                previousData.summary.total_poc_records
            ),
            contract: calculateGrowth(
                data.summary.total_contract_records,
                previousData.summary.total_contract_records
            ),
            close_win: calculateGrowth(
                data.summary.total_close_win_records,
                previousData.summary.total_close_win_records
            ),
            close_lose: calculateGrowth(
                data.summary.total_close_lose_records,
                previousData.summary.total_close_lose_records
            ),
        }
    }, [data, previousData])

    const exportToExcel = () => {
        if (!data) return

        // Summary data - Records (tổng số lần)
        const summaryData = [
            {
                [t('stage')]: t('searching'),
                [t('records')]: data.summary.total_searching_records,
                [t('unique')]: data.summary.total_searching,
                [t('previous_records')]:
                    previousData?.summary.total_searching_records ?? '-',
                [t('growth')]: growthRates?.searching
                    ? `${growthRates.searching >= 0 ? '+' : ''}${growthRates.searching.toFixed(1)}%`
                    : '-',
            },
            {
                [t('stage')]: t('presentation'),
                [t('records')]: data.summary.total_presentation_records,
                [t('unique')]: data.summary.total_presentation,
                [t('previous_records')]:
                    previousData?.summary.total_presentation_records ?? '-',
                [t('growth')]: growthRates?.presentation
                    ? `${growthRates.presentation >= 0 ? '+' : ''}${growthRates.presentation.toFixed(1)}%`
                    : '-',
            },
            {
                [t('stage')]: t('poc'),
                [t('records')]: data.summary.total_poc_records,
                [t('unique')]: data.summary.total_poc,
                [t('previous_records')]:
                    previousData?.summary.total_poc_records ?? '-',
                [t('growth')]: growthRates?.poc
                    ? `${growthRates.poc >= 0 ? '+' : ''}${growthRates.poc.toFixed(1)}%`
                    : '-',
            },
            {
                [t('stage')]: t('contract'),
                [t('records')]: data.summary.total_contract_records,
                [t('unique')]: data.summary.total_contract,
                [t('previous_records')]:
                    previousData?.summary.total_contract_records ?? '-',
                [t('growth')]: growthRates?.contract
                    ? `${growthRates.contract >= 0 ? '+' : ''}${growthRates.contract.toFixed(1)}%`
                    : '-',
            },
            {
                [t('stage')]: t('close_win'),
                [t('records')]: data.summary.total_close_win_records,
                [t('unique')]: data.summary.total_close_win,
                [t('previous_records')]:
                    previousData?.summary.total_close_win_records ?? '-',
                [t('growth')]: growthRates?.close_win
                    ? `${growthRates.close_win >= 0 ? '+' : ''}${growthRates.close_win.toFixed(1)}%`
                    : '-',
            },
            {
                [t('stage')]: t('close_lose'),
                [t('records')]: data.summary.total_close_lose_records,
                [t('unique')]: data.summary.total_close_lose,
                [t('previous_records')]:
                    previousData?.summary.total_close_lose_records ?? '-',
                [t('growth')]: growthRates?.close_lose
                    ? `${growthRates.close_lose >= 0 ? '+' : ''}${growthRates.close_lose.toFixed(1)}%`
                    : '-',
            },
        ]

        const wb = XLSX.utils.book_new()

        // Summary sheet
        const wsSummary = XLSX.utils.json_to_sheet(summaryData)
        XLSX.utils.book_append_sheet(wb, wsSummary, t('pipeline_overview'))

        XLSX.writeFile(
            wb,
            `pipeline_overview_${filters.gte}_${filters.lte}.xlsx`
        )
    }

    const exportToImage = async () => {
        if (!chartRef.current) return

        const canvas = await html2canvas(chartRef.current, {
            backgroundColor: '#ffffff',
            scale: 2,
        })

        const link = document.createElement('a')
        link.download = `pipeline_overview_${filters.gte}_${filters.lte}.png`
        link.href = canvas.toDataURL('image/png')
        link.click()
    }

    if (!data) {
        return (
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100 h-full">
                <div className="flex justify-center items-center h-[300px] text-gray-500">
                    {t('no_data_available')}
                </div>
            </div>
        )
    }

    // Pipeline Overview dùng records (tổng số lần hoạt động)
    // searching = new_contacts, contract = close_win
    const avgDays = data.avg_days
    const overviewData = [
        {
            name: t('funnel.new_contacts'),
            value: data.summary.total_searching_records,
            unique: data.summary.total_searching,
            color: STAGE_COLORS.searching,
            avgDays: null, // Starting point, no avg days
        },
        {
            name: t('funnel.presentation'),
            value: data.summary.total_presentation_records,
            unique: data.summary.total_presentation,
            color: STAGE_COLORS.presentation,
            avgDays: avgDays?.searching_to_presentation,
        },
        {
            name: t('funnel.poc'),
            value: data.summary.total_poc_records,
            unique: data.summary.total_poc,
            color: STAGE_COLORS.poc,
            avgDays: avgDays?.presentation_to_poc,
        },
        {
            name: t('funnel.close_win'),
            value: data.summary.total_close_win_records,
            unique: data.summary.total_close_win,
            color: STAGE_COLORS.close_win,
            avgDays: avgDays?.poc_to_contract,
        },
        {
            name: t('funnel.close_lost'),
            value: data.summary.total_close_lose_records,
            unique: data.summary.total_close_lose,
            color: STAGE_COLORS.close_lose,
            avgDays: null,
        },
    ]

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <div
            ref={chartRef}
            className="bg-white rounded-md p-4 shadow-sm border border-gray-100 h-full"
        >
            <div className="mb-4 flex items-start justify-between">
                <div>
                    <Typogaphy className="font-semibold text-primary">
                        {t('pipeline_overview')}
                    </Typogaphy>
                    <Typogaphy className="text-xs text-gray-500">
                        {t('total_activities_by_stage')}
                    </Typogaphy>
                </div>
                <div className="flex gap-1">
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={exportToImage}
                        className="!min-w-0 !px-2"
                        title={t('download_image')}
                    >
                        <ImageDown size={16} />
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={exportToExcel}
                        className="!min-w-0 !px-2"
                        title={t('download_excel')}
                    >
                        <Download size={16} />
                    </Button>
                </div>
            </div>

            {avgDays?.total_sales_cycle != null && (
                <div className="mb-4 p-3 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-lg border border-indigo-200">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Clock className="w-5 h-5 text-indigo-600" />
                            <Typogaphy className="text-sm font-medium text-gray-700">
                                {t('total_sales_cycle')}
                            </Typogaphy>
                        </div>
                        <div className="flex items-center gap-1">
                            <Typogaphy className="text-2xl font-bold text-indigo-600">
                                {avgDays.total_sales_cycle.toFixed(1)}
                            </Typogaphy>
                            <Typogaphy className="text-sm text-gray-500">
                                {t('days')}
                            </Typogaphy>
                        </div>
                    </div>
                    <Typogaphy className="text-xs text-gray-500 mt-1">
                        {t('avg_time_contacts_to_close_win')}
                    </Typogaphy>
                </div>
            )}

            {/* Bar Chart */}
            <ResponsiveContainer width="100%" height={200}>
                <BarChart
                    data={overviewData}
                    layout="vertical"
                    margin={{ left: 0, right: 20 }}
                >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis type="number" stroke="#9ca3af" fontSize={11} />
                    <YAxis
                        dataKey="name"
                        type="category"
                        stroke="#9ca3af"
                        fontSize={11}
                        width={90}
                        tickLine={false}
                    />
                    <Tooltip
                        content={({ active, payload }) => {
                            if (!active || !payload?.length) return null
                            const item = payload[0].payload
                            return (
                                <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-lg">
                                    <div className="font-medium text-gray-800 mb-1">
                                        {item.name}
                                    </div>
                                    <div className="text-sm text-gray-600">
                                        {item.value.toLocaleString()}{' '}
                                        {t('records')} ({item.unique}{' '}
                                        {t('unique')})
                                    </div>
                                    {item.avgDays != null && (
                                        <div className="text-sm text-indigo-600 mt-1 flex items-center gap-1">
                                            <Clock size={12} />
                                            {t('avg_days')}:{' '}
                                            {item.avgDays.toFixed(1)}{' '}
                                            {t('days')}
                                        </div>
                                    )}
                                </div>
                            )
                        }}
                    />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                        {overviewData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>

            {/* Avg Days per Stage */}
            {avgDays && (
                <div className="mt-2 flex flex-wrap gap-2 text-xs text-gray-600">
                    <div className="flex items-center gap-1 bg-gray-50 px-2 py-1 rounded">
                        <span>Contacts → Presentation:</span>
                        <span className="font-medium text-indigo-600">
                            {avgDays.searching_to_presentation.toFixed(1)}{' '}
                            {t('days')}
                        </span>
                    </div>
                    <div className="flex items-center gap-1 bg-gray-50 px-2 py-1 rounded">
                        <span>Presentation → POC:</span>
                        <span className="font-medium text-indigo-600">
                            {avgDays.presentation_to_poc.toFixed(1)} {t('days')}
                        </span>
                    </div>
                    <div className="flex items-center gap-1 bg-gray-50 px-2 py-1 rounded">
                        <span>POC → Close Win:</span>
                        <span className="font-medium text-indigo-600">
                            {avgDays.poc_to_contract.toFixed(1)} {t('days')}
                        </span>
                    </div>
                </div>
            )}
        </div>
    )
}

export default PipelineOverviewChart
